'use client';

import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Crown,
  Check,
  Star,
  Lightning,
  Shield,
  ArrowLeft,
  Sparkle,
  Clock,
  Database,
  Spinner,
  X,
} from '@phosphor-icons/react';
import * as Dialog from '@radix-ui/react-dialog';
import { QRCodeSVG } from 'qrcode.react';
import { useNavigate } from 'react-router-dom';
import { subscriptionApi } from '../services/api';
import { useToastActions } from '../components/ui/Toast';

const planPrices: Record<string, number> = { free: 0, pro: 99, enterprise: 999 };
const planNames: Record<string, string> = { free: '免费版', pro: '专业版', enterprise: '企业版' };

// 无限符号组件
function InfinityIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M18.178 8c5.096 0 5.096 8 0 8-5.095 0-7.133-8-12.739-8-4.585 0-4.585 8 0 8 5.606 0 7.644-8 12.74-8z" />
    </svg>
  );
}

interface Plan {
  id: 'free' | 'pro' | 'enterprise';
  name: string;
  price: number;
  period: string;
  description: string;
  icon: React.ComponentType<{ className?: string; weight?: 'thin' | 'light' | 'regular' | 'bold' | 'fill' | 'duotone' }>;
  iconColor: string;
  iconBgColor: string;
  features: {
    text: string;
    included: boolean;
  }[];
  popular?: boolean;
}

const plans: Plan[] = [
  {
    id: 'free',
    name: '免费版',
    price: 0,
    period: '/月',
    description: '适合个人用户轻度使用',
    icon: Star,
    iconColor: 'text-zinc-600',
    iconBgColor: 'bg-zinc-100',
    features: [
      { text: '每月 100 次工作流执行', included: true },
      { text: '1GB 数据存储', included: true },
      { text: '基础数据处理功能', included: true },
      { text: '社区支持', included: true },
      { text: '高级数据处理', included: false },
      { text: 'API 访问', included: false },
      { text: '优先技术支持', included: false },
    ],
  },
  {
    id: 'pro',
    name: '专业版',
    price: 99,
    period: '/月',
    description: '适合专业用户日常使用',
    icon: Lightning,
    iconColor: 'text-amber-600',
    iconBgColor: 'bg-amber-100',
    popular: true,
    features: [
      { text: '无限工作流执行', included: true },
      { text: '10GB 数据存储', included: true },
      { text: '基础数据处理功能', included: true },
      { text: '社区支持', included: true },
      { text: '高级数据处理', included: true },
      { text: 'API 访问', included: true },
      { text: '优先技术支持', included: true },
    ],
  },
  {
    id: 'enterprise',
    name: '企业版',
    price: 999,
    period: '/月',
    description: '适合企业级需求',
    icon: Crown,
    iconColor: 'text-purple-600',
    iconBgColor: 'bg-purple-100',
    features: [
      { text: '无限工作流执行', included: true },
      { text: '100GB 数据存储', included: true },
      { text: '所有专业版功能', included: true },
      { text: 'SSO 单点登录', included: true },
      { text: '专属客服支持', included: true },
      { text: '定制化开发', included: true },
      { text: 'SLA 服务保障', included: true },
    ],
  },
];

export function UpgradePage() {
  const navigate = useNavigate();
  const { success: toastSuccess, error: toastError } = useToastActions();
  const [currentPlan, setCurrentPlan] = useState<'free' | 'pro' | 'enterprise'>('free');
  const [selectedPlan, setSelectedPlan] = useState<'free' | 'pro' | 'enterprise'>('pro');
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'yearly'>('monthly');
  const [loading, setLoading] = useState(false);
  const [wechatpayEnabled, setWechatpayEnabled] = useState(false);
  const [showTermsConfirm, setShowTermsConfirm] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentRedirectUrl, setPaymentRedirectUrl] = useState<string | null>(null);
  const [paymentExpireSeconds, setPaymentExpireSeconds] = useState(60);

  useEffect(() => {
    const loadSubscription = async () => {
      try {
        const [subRes, wpRes] = await Promise.all([
          subscriptionApi.getCurrentSubscription(),
          subscriptionApi.isWechatpayEnabled().catch(() => ({ code: 0, data: { enabled: false } })),
        ]);
        if (subRes.code === 0) {
          setCurrentPlan(subRes.data.plan);
          setSelectedPlan(subRes.data.plan === 'free' ? 'pro' : subRes.data.plan);
        }
        if (wpRes?.code === 0 && wpRes?.data) setWechatpayEnabled(wpRes.data.enabled);
      } catch (error) {
        console.error('Failed to load subscription:', error);
      }
    };
    loadSubscription();
  }, []);

  // 支付弹窗倒计时
  useEffect(() => {
    if (!showPaymentModal || paymentExpireSeconds <= 0) return;
    const timer = setInterval(() => setPaymentExpireSeconds((s) => (s <= 1 ? 0 : s - 1)), 1000);
    return () => clearInterval(timer);
  }, [showPaymentModal, paymentExpireSeconds]);

  const closePaymentModal = () => {
    setShowPaymentModal(false);
    setPaymentRedirectUrl(null);
    setPaymentExpireSeconds(60);
  };

  const onTermsConfirm = async () => {
    if (selectedPlan === 'free' || selectedPlan === currentPlan) return;
    setShowTermsConfirm(false);
    const plan = selectedPlan;
    const durationMonths = billingCycle === 'yearly' ? 12 : 1;

    if (wechatpayEnabled) {
      setLoading(true);
      try {
        const res = await subscriptionApi.createWechatpaySign(plan);
        if (res.code === 0 && res.data?.redirectUrl) {
          setPaymentRedirectUrl(res.data.redirectUrl);
          setPaymentExpireSeconds(60);
          setShowPaymentModal(true);
          toastSuccess('请扫码完成微信支付授权');
        } else {
          toastError((res as { message?: string })?.message || '创建签约失败');
        }
      } catch (err: unknown) {
        const msg = err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { data?: { message?: string } } }).response?.data?.message : null;
        toastError(msg || '创建签约失败');
      } finally {
        setLoading(false);
      }
    } else {
      setLoading(true);
      try {
        const res = await subscriptionApi.upgradeSubscription(plan, durationMonths);
        if (res.code === 0) {
          toastSuccess('订阅升级成功！');
          setCurrentPlan(plan);
        }
      } catch (error) {
        toastError('升级失败');
      } finally {
        setLoading(false);
      }
    }
  };

  const handleUpgrade = () => {
    if (selectedPlan === currentPlan) {
      toastSuccess('您已经是该计划的订阅者');
      return;
    }
    if (selectedPlan === 'free') return;
    setShowTermsConfirm(true);
  };

  const getPrice = (plan: Plan) => {
    if (billingCycle === 'yearly') {
      return Math.round(plan.price * 12 * 0.85); // 年付85折
    }
    return plan.price;
  };

  return (
    <div className="h-full bg-zinc-50 overflow-auto">
      <div className="max-w-6xl mx-auto p-6">
        {/* 头部 */}
        <div className="flex items-center gap-4 mb-8">
          <button
            onClick={() => navigate(-1)}
            className="p-2 hover:bg-zinc-200 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-zinc-600" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-zinc-900">订阅升级</h1>
            <p className="text-sm text-zinc-500">选择适合您的计划</p>
          </div>
        </div>

        {/* 当前计划提示 */}
        {currentPlan !== 'free' && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6 p-4 bg-emerald-50 border border-emerald-200 rounded-xl flex items-center gap-3"
          >
            <Sparkle className="w-5 h-5 text-emerald-600" weight="fill" />
            <p className="text-sm text-emerald-800">
              您当前是 <span className="font-semibold">
                {currentPlan === 'pro' ? '专业版' : '企业版'}
              </span> 订阅者
            </p>
          </motion.div>
        )}

        {/* 计费周期切换 */}
        <div className="flex justify-center mb-8">
          <div className="inline-flex bg-white rounded-xl border border-zinc-200 p-1">
            <button
              onClick={() => setBillingCycle('monthly')}
              className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                billingCycle === 'monthly'
                  ? 'bg-zinc-900 text-white'
                  : 'text-zinc-600 hover:text-zinc-900'
              }`}
            >
              月付
            </button>
            <button
              onClick={() => setBillingCycle('yearly')}
              className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors flex items-center gap-2 ${
                billingCycle === 'yearly'
                  ? 'bg-zinc-900 text-white'
                  : 'text-zinc-600 hover:text-zinc-900'
              }`}
            >
              年付
              <span className={`text-xs px-1.5 py-0.5 rounded ${
                billingCycle === 'yearly' ? 'bg-emerald-500 text-white' : 'bg-emerald-100 text-emerald-700'
              }`}>
                省15%
              </span>
            </button>
          </div>
        </div>

        {/* 计划卡片 */}
        <div className="grid grid-cols-3 gap-6 mb-8">
          {plans.map((plan) => {
            const Icon = plan.icon;
            const isSelected = selectedPlan === plan.id;
            const isCurrent = currentPlan === plan.id;
            const price = getPrice(plan);

            return (
              <motion.div
                key={plan.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ type: 'spring', stiffness: 300, damping: 30 }}
                onClick={() => setSelectedPlan(plan.id)}
                className={`relative rounded-2xl border-2 p-6 cursor-pointer transition-all ${
                  isSelected
                    ? 'border-emerald-500 bg-emerald-50/10 shadow-lg shadow-emerald-500/10'
                    : 'border-zinc-200 bg-white hover:border-zinc-300'
                }`}
              >
                {/* 推荐标签 */}
                {plan.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-amber-500 text-white text-xs font-medium rounded-full">
                    最受欢迎
                  </div>
                )}

                {/* 当前计划标签 */}
                {isCurrent && (
                  <div className="absolute -top-3 right-4 px-3 py-1 bg-emerald-500 text-white text-xs font-medium rounded-full">
                    当前计划
                  </div>
                )}

                {/* 图标 */}
                <div className={`w-12 h-12 rounded-xl ${plan.iconBgColor} flex items-center justify-center mb-4`}>
                  <Icon className={`w-6 h-6 ${plan.iconColor}`} weight="fill" />
                </div>

                {/* 计划名称 */}
                <h3 className="text-lg font-bold text-zinc-900 mb-1">{plan.name}</h3>
                <p className="text-xs text-zinc-500 mb-4">{plan.description}</p>

                {/* 价格 */}
                <div className="mb-6">
                  <span className="text-3xl font-bold text-zinc-900">¥{price}</span>
                  <span className="text-sm text-zinc-500">{billingCycle === 'yearly' ? '/年' : plan.period}</span>
                  {billingCycle === 'yearly' && plan.price > 0 && (
                    <p className="text-xs text-zinc-400 mt-1">
                      相当于 ¥{Math.round(price / 12)}/月
                    </p>
                  )}
                </div>

                {/* 功能列表 */}
                <ul className="space-y-3 mb-6">
                  {plan.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start gap-2">
                      {feature.included ? (
                        <Check className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" weight="bold" />
                      ) : (
                        <div className="w-4 h-4 rounded-full border-2 border-zinc-300 flex-shrink-0 mt-0.5" />
                      )}
                      <span className={`text-xs ${feature.included ? 'text-zinc-700' : 'text-zinc-400'}`}>
                        {feature.text}
                      </span>
                    </li>
                  ))}
                </ul>

                {/* 选择按钮：未选中时点击卡片即可选择，已选中且非当前计划时点击触发升级流程 */}
                <button
                  onClick={(e) => {
                    if (isSelected && !isCurrent) {
                      e.stopPropagation();
                      handleUpgrade();
                    }
                  }}
                  className={`w-full py-2.5 text-sm font-medium rounded-xl transition-colors ${
                    isSelected && !isCurrent
                      ? 'bg-emerald-600 text-white hover:bg-emerald-700 cursor-pointer'
                      : isCurrent
                        ? 'bg-emerald-100 text-emerald-700 cursor-default'
                        : 'bg-zinc-100 text-zinc-700 hover:bg-zinc-200 cursor-pointer'
                  }`}
                >
                  {isCurrent ? '当前计划' : isSelected ? '选择此计划' : '选择'}
                </button>
              </motion.div>
            );
          })}
        </div>

        {/* 功能对比 */}
        <div className="bg-white rounded-2xl border border-zinc-200/50 p-6 mb-8">
          <h2 className="text-lg font-semibold text-zinc-900 mb-6">功能对比</h2>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-zinc-100">
                  <th className="text-left py-3 px-4 text-sm font-medium text-zinc-600">功能</th>
                  <th className="text-center py-3 px-4 text-sm font-medium text-zinc-900">免费版</th>
                  <th className="text-center py-3 px-4 text-sm font-medium text-emerald-600">专业版</th>
                  <th className="text-center py-3 px-4 text-sm font-medium text-purple-600">企业版</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-zinc-50">
                  <td className="py-3 px-4 text-zinc-700 flex items-center gap-2">
                    <Clock className="w-4 h-4 text-zinc-400" />
                    工作流执行
                  </td>
                  <td className="text-center py-3 px-4 text-zinc-600">100次/月</td>
                  <td className="text-center py-3 px-4 text-emerald-600 font-medium">
                    <InfinityIcon className="w-4 h-4 inline" />
                  </td>
                  <td className="text-center py-3 px-4 text-purple-600 font-medium">
                    <InfinityIcon className="w-4 h-4 inline" />
                  </td>
                </tr>
                <tr className="border-b border-zinc-50">
                  <td className="py-3 px-4 text-zinc-700 flex items-center gap-2">
                    <Database className="w-4 h-4 text-zinc-400" />
                    数据存储
                  </td>
                  <td className="text-center py-3 px-4 text-zinc-600">1GB</td>
                  <td className="text-center py-3 px-4 text-emerald-600 font-medium">10GB</td>
                  <td className="text-center py-3 px-4 text-purple-600 font-medium">100GB</td>
                </tr>
                <tr className="border-b border-zinc-50">
                  <td className="py-3 px-4 text-zinc-700 flex items-center gap-2">
                    <Shield className="w-4 h-4 text-zinc-400" />
                    技术支持
                  </td>
                  <td className="text-center py-3 px-4 text-zinc-600">社区</td>
                  <td className="text-center py-3 px-4 text-emerald-600 font-medium">优先</td>
                  <td className="text-center py-3 px-4 text-purple-600 font-medium">专属</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        {/* 底部操作栏 */}
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-zinc-200 p-4">
          <div className="max-w-6xl mx-auto flex items-center justify-between">
            <div>
              <p className="text-sm text-zinc-500">已选择</p>
              <p className="text-lg font-bold text-zinc-900">
                {plans.find(p => p.id === selectedPlan)?.name}
                <span className="text-sm font-normal text-zinc-500 ml-2">
                  ¥{getPrice(plans.find(p => p.id === selectedPlan)!)}{billingCycle === 'yearly' ? '/年' : '/月'}
                </span>
              </p>
            </div>
            <button
              onClick={handleUpgrade}
              disabled={loading || selectedPlan === currentPlan}
              className="px-8 py-3 bg-emerald-600 text-white font-medium rounded-xl hover:bg-emerald-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {loading && <Spinner className="w-4 h-4 animate-spin" />}
              {selectedPlan === currentPlan ? '当前计划' : '立即升级'}
            </button>
          </div>
        </div>

        {/* 底部占位 */}
        <div className="h-24" />
      </div>

      {/* 支付条款确认弹窗 */}
      <Dialog.Root open={showTermsConfirm} onOpenChange={(open) => !open && setShowTermsConfirm(false)}>
        <Dialog.Portal>
          <AnimatePresence>
            {showTermsConfirm && (
              <>
                <Dialog.Overlay asChild>
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="fixed inset-0 z-50 bg-zinc-900/40 backdrop-blur-sm"
                  />
                </Dialog.Overlay>
                <Dialog.Content asChild>
                  <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.95 }}
                    className="fixed left-1/2 top-1/2 z-50 w-full max-w-md -translate-x-1/2 -translate-y-1/2 rounded-2xl bg-white p-6 shadow-2xl"
                  >
                    <Dialog.Title className="text-lg font-semibold text-zinc-900 mb-4">
                      订阅确认
                    </Dialog.Title>
                    <Dialog.Description asChild>
                      <p className="text-sm text-zinc-600 mb-6 leading-relaxed">
                        支付即表示您同意
                        <a href="#" className="text-emerald-600 hover:underline mx-1">《会员权益与 AI Excel 付费服务协议》</a>
                      </p>
                    </Dialog.Description>
                    <div className="flex gap-3 justify-end">
                      <button
                        onClick={() => setShowTermsConfirm(false)}
                        className="px-4 py-2 text-sm font-medium text-zinc-600 hover:bg-zinc-100 rounded-lg"
                      >
                        取消
                      </button>
                      <button
                        onClick={() => onTermsConfirm()}
                        className="px-4 py-2 text-sm font-medium bg-emerald-600 text-white rounded-lg hover:bg-emerald-700"
                      >
                        同意并继续
                      </button>
                    </div>
                  </motion.div>
                </Dialog.Content>
              </>
            )}
          </AnimatePresence>
        </Dialog.Portal>
      </Dialog.Root>

      {/* 微信支付弹窗 */}
      <Dialog.Root open={showPaymentModal} onOpenChange={(open) => !open && closePaymentModal()}>
        <Dialog.Portal>
          <AnimatePresence>
            {showPaymentModal && paymentRedirectUrl && selectedPlan !== 'free' && (
              <>
                <Dialog.Overlay asChild>
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="fixed inset-0 z-50 bg-zinc-900/40 backdrop-blur-sm"
                  />
                </Dialog.Overlay>
                <Dialog.Content asChild>
                  <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.95 }}
                    className="fixed left-1/2 top-1/2 z-50 w-full max-w-lg -translate-x-1/2 -translate-y-1/2 rounded-2xl bg-white p-6 shadow-2xl"
                  >
                    <div className="flex items-start justify-between mb-6">
                      <h2 className="text-lg font-semibold text-zinc-900">
                        开通{planNames[selectedPlan]}
                      </h2>
                      <Dialog.Close asChild>
                        <button className="p-2 rounded-lg hover:bg-zinc-100 text-zinc-500">
                          <X className="w-5 h-5" />
                        </button>
                      </Dialog.Close>
                    </div>
                    <div className="flex gap-6">
                      <div className="flex flex-col items-center">
                        <div className="w-48 h-48 bg-white border border-zinc-200 rounded-xl p-3 flex items-center justify-center">
                          <QRCodeSVG value={paymentRedirectUrl} size={180} level="M" />
                        </div>
                        <p className="text-xs text-zinc-500 mt-3">
                          {paymentExpireSeconds > 0 ? `${paymentExpireSeconds} 秒内有效` : '已过期'}
                        </p>
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-4">
                          <div className="w-8 h-8 rounded bg-[#07c160] flex items-center justify-center">
                            <Check className="w-4 h-4 text-white" weight="bold" />
                          </div>
                          <span className="text-sm text-zinc-700">微信支付</span>
                          <span className="text-base font-semibold text-zinc-900">
                            ¥{planPrices[selectedPlan] ?? 0}
                          </span>
                        </div>
                        <ul className="space-y-2 text-xs text-zinc-500">
                          <li>• 自动续费：¥{planPrices[selectedPlan] ?? 0} / 月</li>
                          <li>• 到期前一日通过微信自动扣款，可随时在微信中取消</li>
                          <li>• 虚拟商品一经购买不支持退款</li>
                          <li>• 未成年人请在监护人指导下充值</li>
                          <li>
                            • 支付即表示同意
                            <a href="#" className="text-blue-600 hover:underline">《AI Excel 服务协议》</a>
                          </li>
                        </ul>
                      </div>
                    </div>
                  </motion.div>
                </Dialog.Content>
              </>
            )}
          </AnimatePresence>
        </Dialog.Portal>
      </Dialog.Root>
    </div>
  );
}
